import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

import { useAuth } from '../../contexts/AuthContext';
import { Toaster } from 'react-hot-toast';
import toast from '../../components/ui/Toast';
import analyticsService from '../../services/analyticsService';
import NavigationSidebar from '../../components/NavigationSidebar';
import ErrorBoundary from '../../components/ErrorBoundary';
import TutorialAccessButton from '../../components/TutorialAccessButton';
import ActivityFeed from '../../components/ActivityFeed';
import AgentSelector from './components/AgentSelector';
import MessageComposer from './components/MessageComposer';
import ConversationThread from './components/ConversationThread';
import QuickActions from './components/QuickActions';
import AgentCapabilities from './components/AgentCapabilities';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';


import useAgentChat from '../../hooks/useAgentChat';


import agentService from '../../services/agentService';
import subscriptionService from '../../services/subscriptionService';

const ManagerAgentInterface = () => {
  const navigate = useNavigate();
  const { user, loading: authLoading, currentOrganizationId } = useAuth();
  const [selectedAgent, setSelectedAgent] = useState('');
  const [conversations, setConversations] = useState([]);
  const [selectedThreadId, setSelectedThreadId] = useState(null);
  const [activeTab, setActiveTab] = useState('compose');
  const [tutorialProgress] = useState(45);
  const [loading, setLoading] = useState(true);
  const [currentConversationId, setCurrentConversationId] = useState(null);
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const [isLocked, setIsLocked] = useState(false);
  const [lockReason, setLockReason] = useState('');
  const [showTaskSuccess, setShowTaskSuccess] = useState(false);
  const [lastCreatedTask, setLastCreatedTask] = useState(null);
  const [messageRetryCount, setMessageRetryCount] = useState(0);

  const { response, isLoading: aiLoading, error: aiError, sendMessage } = useAgentChat(
    user?.id,
    selectedAgent,
    currentConversationId,
    true
  );

  const agents = [
    {
      id: 'marketing-ops',
      name: 'Marketing Operations',
      description: 'Content creation and campaign management specialist',
      status: 'active',
      statusMessage: 'Ready to create campaigns and analyze ads',
      icon: 'TrendingUp',
      avatar: 'https://api.dicebear.com/7.x/shapes/svg?seed=marketing',
      avatarAlt: 'Marketing Operations agent avatar with upward trending graph design in purple and blue colors',
      responseTime: '2-5 minutes',
      capabilities: [
        'Generate email campaign templates tailored to your business',
        'Search and analyze high-performing ads in your industry',
        'Create content videos and marketing templates',
        'Store and organize marketing materials in content folder'
      ]
    },
    {
      id: 'personal-productivity',
      name: 'Personal Productivity',
      description: 'Task management and organization expert',
      status: 'active',
      statusMessage: 'Helping you stay organized and efficient',
      icon: 'CheckSquare',
      avatar: 'https://api.dicebear.com/7.x/shapes/svg?seed=productivity',
      avatarAlt: 'Personal Productivity agent avatar with checkmark and list design in green and teal colors',
      responseTime: '1-3 minutes',
      capabilities: [
        'Create and manage daily duty checklists',
        'Organize tasks by priority and deadline',
        'Track progress and completion rates',
        'Send reminders for important tasks'
      ]
    },
    {
      id: 'feedback-analytics',
      name: 'Feedback & Analytics',
      description: 'Performance monitoring and competitive analysis',
      status: 'busy',
      statusMessage: 'Currently analyzing engagement metrics',
      icon: 'BarChart3',
      avatar: 'https://api.dicebear.com/7.x/shapes/svg?seed=analytics',
      avatarAlt: 'Feedback Analytics agent avatar with bar chart and graph design in orange and yellow colors',
      responseTime: '5-10 minutes',
      capabilities: [
        'Analyze user engagement across all channels',
        'Compare performance against competitors',
        'Generate detailed analytics reports',
        'Provide actionable insights and recommendations'
      ]
    },
    {
      id: 'onboarding',
      name: 'Onboarding Agent',
      description: 'Setup and configuration specialist',
      status: 'active',
      statusMessage: 'Ready to help configure your workspace',
      icon: 'Rocket',
      avatar: 'https://api.dicebear.com/7.x/shapes/svg?seed=onboarding',
      avatarAlt: 'Onboarding agent avatar with rocket launch design in red and pink colors',
      responseTime: '3-7 minutes',
      capabilities: [
        'Collect company information and requirements',
        'Configure agent settings and preferences',
        'Guide through initial setup process',
        'Ensure all agents have necessary information'
      ]
    }
  ];

  useEffect(() => {
    if (aiError) {
      // Track AI error
      analyticsService?.trackErrorRecovery(
        'agent_response_failed',
        aiError?.message || 'Failed to get agent response',
        messageRetryCount,
        false,
        selectedAgent
      );
      
      toast?.error('Agent response failed', {
        description: aiError?.message || 'Failed to get agent response',
        duration: 5000
      });
    }
  }, [aiError, messageRetryCount, selectedAgent]);

  useEffect(() => {
    if (user?.id) {
      checkSubscriptionStatus();
      loadConversations();
    }
  }, [user]);

  const checkSubscriptionStatus = async () => {
    if (!user?.id) return;

    try {
      const { data: subscription } = await subscriptionService?.getUserSubscription(user?.id);
      const { data: limitStatus } = await subscriptionService?.getAgentLimitStatus(user?.id);

      setSubscriptionStatus(limitStatus);

      // Check if subscription is expired
      if (subscription?.status === 'expired' || subscription?.status === 'canceled') {
        setIsLocked(true);
        setLockReason('subscription_expired');
        return;
      }

      // Check if agent limit exceeded and grace period expired
      if (limitStatus && !limitStatus?.is_compliant) {
        if (limitStatus?.grace_period_end) {
          const gracePeriodExpired = new Date(limitStatus?.grace_period_end) < new Date();
          if (gracePeriodExpired) {
            setIsLocked(true);
            setLockReason('agent_limit_exceeded');
            return;
          }
        } else {
          setIsLocked(true);
          setLockReason('agent_limit_exceeded');
          return;
        }
      }

      setIsLocked(false);
      setLockReason('');
    } catch (error) {
      console.error('Error checking subscription status:', error);
    }
  };

  const loadConversations = async () => {
    if (!user?.id) return;
    
    setLoading(true);
    try {
      const { data, error } = await agentService?.getConversations(user?.id, currentOrganizationId);
      if (error) throw error;
      
      // Load messages for each conversation
      const conversationsWithMessages = await Promise.all(
        (data || [])?.map(async (conv) => {
          const { data: messages } = await agentService?.getMessages(conv?.id);
          return {
            ...conv,
            messages: messages || [],
            messageCount: messages?.length || 0,
            timestamp: new Date(conv.updated_at)
          };
        })
      );
      
      setConversations(conversationsWithMessages);
    } catch (error) {
      toast?.error('Failed to load conversations', {
        description: 'Unable to retrieve conversation history'
      });
      console.error('Load conversations error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSendMessage = async (messageData) => {
    if (!user?.id) {
      toast?.error('Authentication required', {
        description: 'Please log in to send messages'
      });
      return;
    }

    const loadingToast = toast?.loading('Sending message to agent...');
    const currentRetry = messageRetryCount;

    try {
      // Send message and get AI response
      const result = await sendMessage(messageData?.content, messageData?.priority);
      
      if (result?.conversationId) {
        setCurrentConversationId(result?.conversationId);
        setSelectedThreadId(result?.conversationId);
        setActiveTab('history');
        
        // Reload conversations to show the new one
        await loadConversations();
        
        toast?.dismiss(loadingToast);
        
        // Track successful message send
        analyticsService?.trackMessageSend(
          selectedAgent,
          messageData?.priority,
          true
        );
        
        // If this was a retry, track successful recovery
        if (currentRetry > 0) {
          analyticsService?.trackErrorRecovery(
            'message_send_failed',
            'Message sent successfully after retry',
            currentRetry,
            true,
            selectedAgent
          );
          setMessageRetryCount(0);
        }
        
        toast?.success('Message sent successfully', {
          description: 'Agent is processing your request',
          duration: 3000
        });

        // Show success modal for task creation
        if (messageData?.content?.toLowerCase()?.includes('task') || 
            messageData?.content?.toLowerCase()?.includes('create')) {
          setLastCreatedTask({
            id: result?.conversationId,
            content: messageData?.content,
            agent: selectedAgent
          });
          setShowTaskSuccess(true);
          
          // Track task creation
          analyticsService?.trackTaskCreation(
            selectedAgent,
            'user_requested',
            true
          );
        }
      }
    } catch (error) {
      const newRetryCount = currentRetry + 1;
      setMessageRetryCount(newRetryCount);
      
      toast?.dismiss(loadingToast);
      
      // Track message send failure
      analyticsService?.trackMessageSend(
        selectedAgent,
        messageData?.priority,
        false
      );
      
      // Track error recovery attempt
      analyticsService?.trackErrorRecovery(
        'message_send_failed',
        error?.message || 'Failed to send message',
        newRetryCount,
        false,
        selectedAgent
      );
      
      // Track friction point
      analyticsService?.trackFrictionPoint(
        'agent_messaging',
        'send_message_error',
        4,
        { agent: selectedAgent, retry_count: newRetryCount }
      );
      
      toast?.error('Failed to send message', {
        description: error?.message || 'Please try again',
        action: {
          label: 'Retry',
          onClick: () => handleSendMessage(messageData)
        },
        duration: 5000
      });
      console.error('Send message error:', error);
    }
  };

  const handleQuickAction = (actionId) => {
    switch (actionId) {
      case 'status': console.log('Checking agent status...');
        break;
      case 'history': setActiveTab('history');
        break;
      case 'template': console.log('Loading message template...');
        break;
      case 'schedule': console.log('Opening task scheduler...');
        break;
      default:
        break;
    }
  };

  const handleTutorialStart = () => {
    console.log('Starting tutorial...');
  };

  const handleUpgradeClick = () => {
    // Track upgrade CTA click from locked interface
    analyticsService?.trackUpgradeCTA(
      'manager_agent_locked',
      subscriptionStatus?.current_tier || 'unknown'
    );
    
    navigate('/pricing');
  };

  const selectedAgentData = agents?.find(a => a?.id === selectedAgent);

  // Locked state UI
  if (isLocked) {
    return (
      <div className="min-h-screen bg-background">
        <Toaster position="top-right" />
        <NavigationSidebar />
        <div className="lg:pl-60 pb-16 lg:pb-0">
          <div className="sticky top-0 z-10 bg-card border-b border-border px-4 md:px-6 lg:px-8 py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 md:w-12 md:h-12 rounded-xl bg-primary/10 flex items-center justify-center">
                  <Icon name="Users" size={24} color="var(--color-primary)" />
                </div>
                <div>
                  <h1 className="text-xl md:text-2xl lg:text-3xl font-bold text-foreground">
                    Manager Agent Interface
                  </h1>
                  <p className="text-sm md:text-base text-muted-foreground">
                    Coordinate tasks across specialized agents
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="p-4 md:p-6 lg:p-8">
            <div className="max-w-2xl mx-auto">
              <div className="bg-card border-2 border-destructive/20 rounded-xl p-8 text-center">
                <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-destructive/10 flex items-center justify-center">
                  <Icon name="Lock" size={40} className="text-destructive" />
                </div>
                
                <h2 className="text-2xl font-bold text-foreground mb-3">
                  {lockReason === 'subscription_expired' ? 'Subscription Expired' : 'Agent Limit Exceeded'}
                </h2>
                
                <p className="text-muted-foreground mb-6 text-lg">
                  {lockReason === 'subscription_expired' ?'Your subscription has expired. Please upgrade to continue using the Manager Agent Interface.'
                    : `You have exceeded your agent limit${subscriptionStatus?.grace_period_end ? ' and the grace period has expired' : ''}. Please upgrade your plan to access more agents.`}
                </p>

                {subscriptionStatus && (
                  <div className="bg-muted/50 rounded-lg p-4 mb-6">
                    <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground mb-2">
                      <Icon name="Users" size={16} />
                      <span>Current Usage</span>
                    </div>
                    <div className="text-3xl font-bold text-foreground">
                      {subscriptionStatus?.active_agents} / {subscriptionStatus?.max_agents}
                    </div>
                    <div className="text-sm text-muted-foreground mt-1">
                      Active Agents
                    </div>
                  </div>
                )}

                <div className="flex flex-col sm:flex-row gap-3 justify-center">
                  <Button
                    variant="default"
                    size="lg"
                    onClick={handleUpgradeClick}
                    iconName="ArrowRight"
                    iconPosition="right"
                  >
                    Upgrade Now
                  </Button>
                  <Button
                    variant="outline"
                    size="lg"
                    onClick={() => navigate('/dashboard')}
                    iconName="ArrowLeft"
                    iconPosition="left"
                  >
                    Back to Dashboard
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <ErrorBoundary>
      <div className="min-h-screen bg-background">
        <Toaster position="top-right" />
        <NavigationSidebar />
        <div className="lg:pl-60 pb-16 lg:pb-0">
          <div className="sticky top-0 z-10 bg-card border-b border-border px-4 md:px-6 lg:px-8 py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 md:w-12 md:h-12 rounded-xl bg-primary/10 flex items-center justify-center">
                  <Icon name="Users" size={24} color="var(--color-primary)" />
                </div>
                <div>
                  <h1 className="text-xl md:text-2xl lg:text-3xl font-bold text-foreground">
                    Manager Agent Interface
                  </h1>
                  <p className="text-sm md:text-base text-muted-foreground">
                    Coordinate tasks across specialized agents
                  </p>
                </div>
              </div>
              <TutorialAccessButton 
                onTutorialStart={handleTutorialStart}
                tutorialProgress={tutorialProgress}
                className="hidden md:block"
              />
            </div>
          </div>

          <div className="p-4 md:p-6 lg:p-8">
            {loading ? (
              <div className="flex items-center justify-center h-full">
                <div className="text-foreground text-lg">Loading...</div>
              </div>
            ) : (
              <>
                <div className="lg:hidden mb-6">
                  <div className="flex gap-2 overflow-x-auto pb-2">
                    <button
                      onClick={() => setActiveTab('compose')}
                      className={`
                        flex items-center gap-2 px-4 py-2 rounded-lg font-medium text-sm whitespace-nowrap transition-all duration-250
                        ${activeTab === 'compose' ?'bg-primary text-primary-foreground shadow-warm-sm' :'bg-muted text-muted-foreground'
                        }
                      `}
                    >
                      <Icon name="Edit3" size={16} />
                      Compose
                    </button>
                    <button
                      onClick={() => setActiveTab('history')}
                      className={`
                        flex items-center gap-2 px-4 py-2 rounded-lg font-medium text-sm whitespace-nowrap transition-all duration-250
                        ${activeTab === 'history' ?'bg-primary text-primary-foreground shadow-warm-sm' :'bg-muted text-muted-foreground'
                        }
                      `}
                    >
                      <Icon name="MessageSquare" size={16} />
                      History ({conversations?.length})
                    </button>
                    <button
                      onClick={() => setActiveTab('activity')}
                      className={`
                        flex items-center gap-2 px-4 py-2 rounded-lg font-medium text-sm whitespace-nowrap transition-all duration-250
                        ${activeTab === 'activity' ?'bg-primary text-primary-foreground shadow-warm-sm' :'bg-muted text-muted-foreground'
                        }
                      `}
                    >
                      <Icon name="Activity" size={16} />
                      Activity
                    </button>
                  </div>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-8">
                  <div className={`lg:col-span-5 space-y-6 ${activeTab !== 'compose' ? 'hidden lg:block' : ''}`}>
                    <div className="bg-card border border-border rounded-xl p-6 shadow-warm">
                      <div className="flex items-center gap-3 mb-6">
                        <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                          <Icon name="Edit3" size={20} color="var(--color-primary)" />
                        </div>
                        <div>
                          <h2 className="text-lg font-semibold text-foreground">Compose Message</h2>
                          <p className="text-sm text-muted-foreground">Direct tasks to specialized agents</p>
                        </div>
                      </div>

                      <div className="space-y-6">
                        <AgentSelector 
                          selectedAgent={selectedAgent}
                          onAgentChange={setSelectedAgent}
                          agents={agents}
                        />

                        <MessageComposer 
                          selectedAgent={selectedAgent}
                          onSendMessage={handleSendMessage}
                          agents={agents}
                        />
                      </div>
                    </div>

                    <QuickActions 
                      onActionClick={handleQuickAction}
                      selectedAgent={selectedAgent}
                    />

                    <AgentCapabilities agent={selectedAgentData} />
                  </div>

                  <div className={`lg:col-span-7 space-y-6 ${activeTab !== 'history' ? 'hidden lg:block' : ''}`}>
                    <div className="bg-card border border-border rounded-xl p-6 shadow-warm">
                      <div className="flex items-center justify-between mb-6">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-lg bg-accent/10 flex items-center justify-center">
                            <Icon name="MessageSquare" size={20} color="var(--color-accent)" />
                          </div>
                          <div>
                            <h2 className="text-lg font-semibold text-foreground">Conversation History</h2>
                            <p className="text-sm text-muted-foreground">
                              {conversations?.length} active {conversations?.length === 1 ? 'thread' : 'threads'}
                            </p>
                          </div>
                        </div>
                      </div>

                      <div className="max-h-[600px] lg:max-h-[700px] overflow-y-auto custom-scrollbar pr-2">
                        <ConversationThread 
                          conversations={conversations}
                          agents={agents}
                          onThreadSelect={setSelectedThreadId}
                          selectedThreadId={selectedThreadId}
                        />
                      </div>
                    </div>
                  </div>

                  <div className={`lg:hidden ${activeTab !== 'activity' ? 'hidden' : ''}`}>
                    <ActivityFeed maxItems={20} />
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </ErrorBoundary>
  );
};

export default ManagerAgentInterface;